import Head from 'design-system/Head';
import {
  getLookupCustomerAuditHistoryProps,
  getLookupInternalAuditHistoryProps,
} from 'react-scale/corp/components/Lookup/BaseLookupObject';
import { Extras } from 'react-scale/corp/components/Lookup/LookupTypes';
import CustomerAuditHistory from 'react-scale/corp/components/Lookup/sections/CustomerAuditHistory';
import InternalAuditHistory from 'react-scale/corp/components/Lookup/sections/InternalAuditHistory';
import { PipelineNodeGraph } from 'react-scale/corp/components/Lookup/sections/PipelineV3Graphs';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import HideableSection from 'react-scale/corp/components/UI/HideableSection';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Table, TableColumn, Typography } from '@scale/scaleui';
import { ISensorFusionStageInstanceLookupDto } from '@scale/sensor-fusion-shared';

import { openSelectedIfUrl } from '../helpers/url';
import { LookupIdLink } from '../common';
import StageInstanceOverview from './StageInstanceOverview';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type StageInstanceLookupViewProps = {
  extras: Partial<Extras>;
  stageInstance: ISensorFusionStageInstanceLookupDto;
};

const RESPONSE_TABLE_COLUMNS: TableColumn[] = [
  { header: 'Substage Instance', id: 'substageInstance' },
  { header: 'Response URL', id: 'url' },
];

const StageInstanceLookupView = ({ extras, stageInstance }: StageInstanceLookupViewProps) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const internalAuditHistoryProps = getLookupInternalAuditHistoryProps({ object: stageInstance });
  const customerAuditHistoryProps = getLookupCustomerAuditHistoryProps({
    object: stageInstance,
    scalerAccess: extras.scalerAccess,
  });

  const hasPipelineNodes = !!stageInstance.pipelineNodes?.length;
  const hasLotsOfPipelineNodes = hasPipelineNodes && stageInstance.pipelineNodes!.length > 75;

  const responseTableData = stageInstance.responses.map(response => ({
    substageInstance: (
      <>
        <span className="mr-1">{response.substageInstance.defName}</span>
        <LookupIdLink className="inline-block" id={response.substageInstance.srn} />
      </>
    ),
    url: (
      <WithCopy copyText={response.url}>
        <a className="text-info-700" href={response.url}>
          {response.url}
        </a>
      </WithCopy>
    ),
  }));

  return (
    <div className="scaleui scale-tailwind scale-theme w-max-full w-full overflow-scroll px-8">
      <Head title={`Stage Instance ${stageInstance._id} ${stageInstance.stageDefinition.name}`} />
      <Typography.H1>Stage Instance</Typography.H1>
      <div className="mx-1">
        <WithCopy className="inline-block" copyText={stageInstance._id}>
          {stageInstance._id}
        </WithCopy>
      </div>

      <StageInstanceOverview stageInstance={stageInstance} />

      {hasPipelineNodes && (
        <HideableSection
          name="Pipeline Execution Overview Graph"
          forceHidden={hasLotsOfPipelineNodes}
          containZoom
        >
          <PipelineNodeGraph
            nodes={stageInstance.pipelineNodes!}
            executionId={stageInstance.pipelineExecutionId}
          />
        </HideableSection>
      )}

      <HideableSection name="Responses">
        <Table
          columns={RESPONSE_TABLE_COLUMNS}
          data={responseTableData}
          divideRows
          manualPagination
        />
      </HideableSection>

      {customerAuditHistoryProps && (
        <HideableSection name="Customer Audit History">
          <CustomerAuditHistory {...customerAuditHistoryProps} />
        </HideableSection>
      )}

      {internalAuditHistoryProps && (
        <HideableSection name="Internal Audit History">
          <InternalAuditHistory {...internalAuditHistoryProps} />
        </HideableSection>
      )}

      {!isRemoAdmin && (
        <HideableSection name="JSON">
          <ReactJsonView
            src={stageInstance}
            name={false}
            theme="monokai"
            iconStyle="triangle"
            collapsed={1}
            onSelect={openSelectedIfUrl}
            collapseStringsAfterLength={120}
            groupArraysAfterLength={false}
            enableClipboard={true}
            displayObjectSize={false}
            displayDataTypes={false}
          />
        </HideableSection>
      )}
    </div>
  );
};

export default StageInstanceLookupView;
